import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [subscription, setSubscription] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      // After max attempts, check subscription status directly
      await checkSubscriptionStatus();
      return;
    }

    try {
      const res = await fetch(`${API}/api/subscriptions/checkout-status/${sessionId}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Failed to check status');
      }

      const data = await res.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setSubscription(data);
        return;
      }

      if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const res = await fetch(`${API}/api/users/me/subscription`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        if (data.has_subscription) {
          setStatus('success');
          setSubscription({
            tier_name: data.tier?.display_name || data.subscription?.tier_name
          });
          return;
        }
      }
      setStatus('failed');
    } catch (error) {
      setStatus('failed');
    }
  };

  const handleRetry = () => {
    setAttempts(0);
    setStatus('checking');
    pollPaymentStatus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {status === 'checking' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-lg">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Processing Your Subscription
            </h1>
            <p className="text-slate-600 mb-4">
              Please wait while we confirm your payment...
            </p>
            <div className="flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i < attempts ? 'bg-primary' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-lg">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Welcome to {subscription?.tier_name || 'Your Plan'}!
            </h1>
            <p className="text-slate-600 mb-8">
              Your subscription is now active. You have full access to all features included in your plan.
            </p>
            <div className="space-y-3">
              <Button
                data-testid="go-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="explore-case-studies"
                onClick={() => navigate('/case-studies')}
                variant="outline"
                className="w-full"
              >
                Explore Case Studies
              </Button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Something Went Wrong
            </h1>
            <p className="text-slate-600 mb-8">
              We couldn't confirm your payment. If you were charged, your subscription may still be processing.
            </p>
            <div className="space-y-3">
              <Button
                data-testid="retry-check"
                onClick={handleRetry}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry Verification
              </Button>
              <Button
                data-testid="back-to-pricing"
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="w-full"
              >
                Back to Pricing
              </Button>
              <p className="text-sm text-slate-500 mt-4">
                Need help? <a href="/contact" className="text-primary hover:underline">Contact Support</a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
